/**
 * Печать краткой карточки страны ГО (HTML → iframe → window.print / «Сохранить как PDF»).
 * Паттерн как у `rd-intersection-report-print.ts`.
 */

export type GoCountryPassportPrintRow = {
  label: string;
  value: string;
};

export type GoCountryPassportPrintInput = {
  ginfObjId: number;
  objName: string;
  entityTypeName?: string | null;
  templateName?: string | null;
  mapGeometryCount?: number | null;
  passportRows: GoCountryPassportPrintRow[];
  flagDataUrl?: string | null;
  coatDataUrl?: string | null;
  /** GeoJSON Feature[] контуров страны для SVG-схемы. */
  geojsonFeatures: GeoJSON.Feature[];
  generatedAt?: Date;
};

function escapeHtml(value: unknown): string {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function formatDateTimeRu(d: Date): string {
  return d.toLocaleString("ru-RU", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function collectCoords(geom: GeoJSON.Geometry | null | undefined, out: [number, number][]): void {
  if (!geom) return;
  switch (geom.type) {
    case "Point":
      out.push([geom.coordinates[0], geom.coordinates[1]]);
      break;
    case "MultiPoint":
    case "LineString":
      for (const c of geom.coordinates) out.push([c[0], c[1]]);
      break;
    case "MultiLineString":
    case "Polygon":
      for (const ring of geom.coordinates) {
        for (const c of ring) out.push([c[0], c[1]]);
      }
      break;
    case "MultiPolygon":
      for (const poly of geom.coordinates) {
        for (const ring of poly) {
          for (const c of ring) out.push([c[0], c[1]]);
        }
      }
      break;
    case "GeometryCollection":
      for (const g of geom.geometries) collectCoords(g, out);
      break;
    default:
      break;
  }
}

function ringToPath(
  ring: GeoJSON.Position[],
  project: (lon: number, lat: number) => [number, number],
  close: boolean,
): string {
  if (ring.length === 0) return "";
  const parts: string[] = [];
  ring.forEach((c, i) => {
    const [x, y] = project(c[0], c[1]);
    parts.push(`${i === 0 ? "M" : "L"}${x.toFixed(1)} ${y.toFixed(1)}`);
  });
  if (close) parts.push("Z");
  return parts.join(" ");
}

function geometryToPath(
  geom: GeoJSON.Geometry,
  project: (lon: number, lat: number) => [number, number],
): string {
  switch (geom.type) {
    case "Polygon":
      return geom.coordinates.map((ring) => ringToPath(ring, project, true)).join(" ");
    case "MultiPolygon":
      return geom.coordinates
        .map((poly) => poly.map((ring) => ringToPath(ring, project, true)).join(" "))
        .join(" ");
    case "LineString":
      return ringToPath(geom.coordinates, project, false);
    case "MultiLineString":
      return geom.coordinates.map((line) => ringToPath(line, project, false)).join(" ");
    case "Point": {
      const [x, y] = project(geom.coordinates[0], geom.coordinates[1]);
      return `M${(x - 3).toFixed(1)} ${y.toFixed(1)} a3 3 0 1 0 6 0 a3 3 0 1 0 -6 0`;
    }
    case "MultiPoint":
      return geom.coordinates
        .map((c) => {
          const [x, y] = project(c[0], c[1]);
          return `M${(x - 3).toFixed(1)} ${y.toFixed(1)} a3 3 0 1 0 6 0 a3 3 0 1 0 -6 0`;
        })
        .join(" ");
    case "GeometryCollection":
      return geom.geometries.map((g) => geometryToPath(g, project)).join(" ");
    default:
      return "";
  }
}

/** SVG-схема контуров в viewBox 640×360 (долгота/широта → пиксели). */
export function buildGeometrySvg(features: GeoJSON.Feature[]): string {
  const pts: [number, number][] = [];
  for (const f of features) {
    collectCoords(f.geometry, pts);
  }
  if (pts.length === 0) {
    return `<div class="map-empty">Геометрия контура отсутствует</div>`;
  }

  let minLon = Infinity;
  let minLat = Infinity;
  let maxLon = -Infinity;
  let maxLat = -Infinity;
  for (const [lon, lat] of pts) {
    minLon = Math.min(minLon, lon);
    maxLon = Math.max(maxLon, lon);
    minLat = Math.min(minLat, lat);
    maxLat = Math.max(maxLat, lat);
  }
  const padRatio = 0.08;
  const lonSpan = Math.max(maxLon - minLon, 1e-6);
  const latSpan = Math.max(maxLat - minLat, 1e-6);
  const padLon = lonSpan * padRatio;
  const padLat = latSpan * padRatio;
  minLon -= padLon;
  maxLon += padLon;
  minLat -= padLat;
  maxLat += padLat;

  const W = 640;
  const H = 360;
  const project = (lon: number, lat: number): [number, number] => {
    const x = ((lon - minLon) / (maxLon - minLon)) * W;
    const y = (1 - (lat - minLat) / (maxLat - minLat)) * H;
    return [x, y];
  };

  const paths = features
    .map((f) => {
      if (!f.geometry) return "";
      const d = geometryToPath(f.geometry, project);
      if (!d) return "";
      const isLine =
        f.geometry.type === "LineString" || f.geometry.type === "MultiLineString";
      const isPoint = f.geometry.type === "Point" || f.geometry.type === "MultiPoint";
      if (isPoint) {
        return `<path d="${d}" fill="#0f4c81" stroke="none" />`;
      }
      if (isLine) {
        return `<path d="${d}" fill="none" stroke="#0f4c81" stroke-width="2" stroke-linejoin="round" />`;
      }
      return `<path d="${d}" fill="rgba(15,76,129,0.28)" stroke="#0f4c81" stroke-width="1.5" stroke-linejoin="round" />`;
    })
    .filter(Boolean)
    .join("\n");

  return `<svg class="map-svg" viewBox="0 0 ${W} ${H}" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="Схема контура страны">
  <rect width="${W}" height="${H}" fill="#e8f1f8" rx="6" />
  ${paths}
</svg>`;
}

function passportRowsHtml(rows: GoCountryPassportPrintRow[]): string {
  if (rows.length === 0) {
    return `<tr><td colspan="2" class="empty">Нет данных паспорта</td></tr>`;
  }
  return rows
    .map(
      (r) => `<tr>
      <td class="label">${escapeHtml(r.label)}</td>
      <td>${escapeHtml(r.value)}</td>
    </tr>`,
    )
    .join("\n");
}

function buildReportHtml(input: GoCountryPassportPrintInput): string {
  const generatedAt = input.generatedAt ?? new Date();
  const title = input.objName || `Сущность #${input.ginfObjId}`;
  const symbols = [
    input.flagDataUrl
      ? `<figure class="symbol"><img src="${input.flagDataUrl}" alt="Флаг" /><figcaption>Флаг</figcaption></figure>`
      : "",
    input.coatDataUrl
      ? `<figure class="symbol"><img src="${input.coatDataUrl}" alt="Герб" /><figcaption>Герб</figcaption></figure>`
      : "",
  ]
    .filter(Boolean)
    .join("\n");

  const mapBlock = buildGeometrySvg(input.geojsonFeatures);

  return `<!DOCTYPE html>
<html lang="ru">
<head>
  <meta charset="utf-8" />
  <title>Карточка страны — ${escapeHtml(title)}</title>
  <style>
    :root {
      --ink: #1a2332;
      --muted: #5c6b7a;
      --line: #d8dee6;
      --brand: #0f4c81;
      --brand-soft: #e8f1f8;
    }
    * { box-sizing: border-box; }
    body {
      margin: 0;
      padding: 28px 32px 40px;
      font-family: "Segoe UI", "Liberation Sans", Arial, sans-serif;
      color: var(--ink);
      font-size: 12.5px;
      line-height: 1.45;
      background: #fff;
    }
    .header {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      gap: 24px;
      border-bottom: 3px solid var(--brand);
      padding-bottom: 16px;
      margin-bottom: 22px;
    }
    .brand {
      font-size: 11px;
      letter-spacing: 0.08em;
      text-transform: uppercase;
      color: var(--brand);
      font-weight: 700;
      margin-bottom: 6px;
    }
    h1 {
      margin: 0;
      font-size: 20px;
      font-weight: 700;
    }
    .subtitle { margin-top: 4px; color: var(--muted); font-size: 12px; }
    .meta-stamp {
      text-align: right;
      color: var(--muted);
      font-size: 11px;
      white-space: nowrap;
    }
    .meta-stamp strong { color: var(--ink); display: block; font-size: 12px; margin-bottom: 2px; }
    .grid {
      display: grid;
      grid-template-columns: 1.2fr 0.8fr;
      gap: 14px 20px;
      margin-bottom: 20px;
    }
    .card {
      border: 1px solid var(--line);
      border-radius: 8px;
      padding: 12px 14px;
      background: #fafbfc;
    }
    .card h2 {
      margin: 0 0 10px;
      font-size: 11px;
      text-transform: uppercase;
      letter-spacing: 0.06em;
      color: var(--brand);
    }
    .kv { display: grid; grid-template-columns: 150px 1fr; gap: 6px 10px; }
    .kv dt { color: var(--muted); }
    .kv dd { margin: 0; font-weight: 600; }
    .symbols {
      display: flex;
      flex-wrap: wrap;
      gap: 12px;
      align-items: flex-start;
      justify-content: center;
    }
    .symbol {
      margin: 0;
      text-align: center;
    }
    .symbol img {
      display: block;
      max-height: 96px;
      max-width: 140px;
      object-fit: contain;
      border: 1px solid var(--line);
      border-radius: 4px;
      background: #fff;
    }
    .symbol figcaption {
      margin-top: 4px;
      font-size: 10px;
      color: var(--muted);
      text-transform: uppercase;
      letter-spacing: 0.04em;
    }
    .section-title {
      margin: 8px 0 8px;
      font-size: 14px;
      font-weight: 700;
    }
    table {
      width: 100%;
      border-collapse: collapse;
      margin-top: 4px;
    }
    th, td {
      border: 1px solid var(--line);
      padding: 8px 9px;
      vertical-align: top;
      text-align: left;
    }
    th {
      background: var(--brand);
      color: #fff;
      font-weight: 600;
      font-size: 11px;
      text-transform: uppercase;
      letter-spacing: 0.04em;
    }
    td.label { width: 40%; color: var(--muted); }
    tr:nth-child(even) td { background: #f7f9fb; }
    td.empty { text-align: center; color: var(--muted); padding: 18px; }
    .map-wrap {
      margin-top: 8px;
      border: 1px solid var(--line);
      border-radius: 8px;
      overflow: hidden;
      background: var(--brand-soft);
      padding: 8px;
    }
    .map-svg { width: 100%; height: auto; display: block; }
    .map-empty {
      padding: 28px;
      text-align: center;
      color: var(--muted);
      font-size: 13px;
    }
    .map-caption {
      margin-top: 6px;
      font-size: 11px;
      color: var(--muted);
    }
    .footnote {
      margin-top: 22px;
      padding-top: 12px;
      border-top: 1px solid var(--line);
      color: var(--muted);
      font-size: 10.5px;
    }
    .print-hint {
      margin-bottom: 14px;
      padding: 10px 12px;
      background: #fffbeb;
      border: 1px solid #fcd34d;
      border-radius: 6px;
      color: #92400e;
      font-size: 12px;
    }
    @media print {
      body { padding: 12mm 10mm; }
      .print-hint { display: none !important; }
      .card { break-inside: avoid; }
      .map-wrap { break-inside: avoid; }
    }
    @media (max-width: 720px) {
      .grid { grid-template-columns: 1fr; }
    }
  </style>
</head>
<body>
  <div class="print-hint">
    Для сохранения в PDF выберите в диалоге печати принтер «Сохранить как PDF» / «Microsoft Print to PDF», затем «Печать».
  </div>
  <header class="header">
    <div>
      <div class="brand">GeoPulseML · модуль ГО</div>
      <h1>Карточка страны</h1>
      <div class="subtitle">Краткий паспорт и контур территории</div>
    </div>
    <div class="meta-stamp">
      <strong>Сформировано</strong>
      ${escapeHtml(formatDateTimeRu(generatedAt))}
    </div>
  </header>

  <div class="grid">
    <section class="card">
      <h2>Сущность</h2>
      <dl class="kv">
        <dt>Наименование</dt><dd>${escapeHtml(title)}</dd>
        <dt>ID (ginf_obj_id)</dt><dd>${input.ginfObjId}</dd>
        <dt>Тип</dt><dd>${escapeHtml(input.entityTypeName || "—")}</dd>
        <dt>Геометрий</dt><dd>${input.mapGeometryCount != null ? input.mapGeometryCount : "—"}</dd>
        <dt>Шаблон</dt><dd>${escapeHtml(input.templateName || "—")}</dd>
      </dl>
    </section>
    <section class="card">
      <h2>Государственная символика</h2>
      ${
        symbols
          ? `<div class="symbols">${symbols}</div>`
          : `<p style="margin:0;color:var(--muted)">Флаг и герб не загружены</p>`
      }
    </section>
  </div>

  <h2 class="section-title">Паспортные сведения</h2>
  <table>
    <thead>
      <tr><th>Показатель</th><th>Значение</th></tr>
    </thead>
    <tbody>
      ${passportRowsHtml(input.passportRows)}
    </tbody>
  </table>

  <h2 class="section-title">Геометрия на карте</h2>
  <div class="map-wrap">
    ${mapBlock}
    <p class="map-caption">Схема контура по данным модуля ГО (без подложки тайлов). Для полной картографической картины используйте карту workspace.</p>
  </div>

  <footer class="footnote">
    Документ сформирован в рабочем контуре модуля ГО (географическое описание).
    Предназначен для служебного использования. Не заменяет полный набор полей ok20.
  </footer>
</body>
</html>`;
}

export async function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error ?? new Error("FileReader failed"));
    reader.readAsDataURL(blob);
  });
}

/**
 * Печать через скрытый iframe (без popup): диалог печати / «Сохранить как PDF».
 */
export function printGoCountryPassportReport(input: GoCountryPassportPrintInput): void {
  const html = buildReportHtml(input);
  const iframe = document.createElement("iframe");
  iframe.setAttribute("title", "Печать карточки страны");
  iframe.style.cssText =
    "position:fixed;right:0;bottom:0;width:0;height:0;border:0;opacity:0;pointer-events:none;";
  document.body.appendChild(iframe);

  const frameWindow = iframe.contentWindow;
  const frameDoc = frameWindow?.document ?? iframe.contentDocument;
  if (!frameWindow || !frameDoc) {
    iframe.remove();
    throw new Error("Не удалось подготовить документ для печати.");
  }

  frameDoc.open();
  frameDoc.write(html);
  frameDoc.close();

  const cleanup = () => {
    iframe.remove();
  };

  const trigger = () => {
    try {
      frameWindow.focus();
      frameWindow.print();
    } finally {
      window.setTimeout(cleanup, 1000);
    }
  };

  // Дать браузеру отрисовать data-URL изображений перед print.
  const delayMs = input.flagDataUrl || input.coatDataUrl ? 250 : 100;
  if (frameDoc.readyState === "complete") {
    window.setTimeout(trigger, delayMs);
  } else {
    iframe.addEventListener("load", () => window.setTimeout(trigger, delayMs), { once: true });
  }
}
